<template>
  <v-row no-gutters>
    <v-col
      class="mr-md-12 pa-3 pa-md-0 mt-md-16 mt-12"
      :class="{ 'flex-1-1-100': $vuetify.display.mdAndDown }"
    >
      <div v-if="allTicketSelectionData && localSelectionsMap" class="d-lg-none mb-8">
        <v-row no-gutters justify="center" class="mb-6">
          <h2 class="fs-24">SELECT YOUR REGISTRATION</h2>
        </v-row>
        <TicketCard
          v-for="ticket of visibleTicketSelection"
          :key="ticket.ticket_id"
          :event-data="eventData"
          :ticket="ticket"
          :selected-tickets="localSelectionsMap"
          :qty="localSelectionsMap[`${ticket.table_id}_${ticket.ticket_id}`]?.qty || 0"
          @update:qty="(qty, revert) => handleQtyChange(ticket, qty, revert)"
        />
        <div v-if="allTicketSelectionData.length > ticketsToShow">
          <v-btn
            block
            color="#A3A3A3"
            variant="text"
            class="mt-2 text-transform-none"
            @click.stop="ticketsToShow += 2"
            append-icon="mdi-chevron-down"
          >
            Show more
          </v-btn>
        </div>
        <v-divider class="my-8"></v-divider>
      </div>

      <div v-if="isEmpty(tables)">
        <v-card variant="outlined" style="border: 1px solid #e5e5e5">
          <v-card-text class="pa-2 text-center">
            <h2 class="fs-18 font-weight-medium" style="color: #262626">
              Please make a selection to begin
            </h2>
          </v-card-text>
        </v-card>
      </div>

      <v-expansion-panels v-else v-model="openExpansion" multiple>
        <template v-if="tables.length > 0">
          <v-expansion-panel class="mb-6" :value="0">
            <v-expansion-panel-title>
              <v-row no-gutters>
                <h2 class="fs-18 font-weight-medium mb-2">Attendee Details</h2>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="pa-0 pa-md-4">
                <v-col md="10" class="d-flex align-start flex-column ga-4">
                  <p class="fs-16">
                    Enter the Primary Registrant's information. They will receive a confirmation
                    email containing their registration details and guest list.
                  </p>
                  <div class="d-flex align-left items-center justify-start">
                    <div class="mr-3">
                      <v-tooltip location="top">
                        <template #activator="{ props }">
                          <v-icon
                            v-bind="props"
                            :color="
                              !!stashedData[primaryRegistrantTable?._instanceId]
                                ? 'success'
                                : 'warning'
                            "
                            size="small"
                          >
                            {{
                              !!stashedData[primaryRegistrantTable?._instanceId]
                                ? 'mdi-check-circle'
                                : 'mdi-alert-circle'
                            }}
                          </v-icon>
                        </template>
                        <span>
                          {{
                            !!stashedData[primaryRegistrantTable?._instanceId]
                              ? 'Form Completed'
                              : 'Form Not Completed'
                          }}
                        </span>
                      </v-tooltip>
                    </div>
                    <p class="fs-16 font-italic">Primary Registrant:</p>
                  </div>
                  <div v-if="hasMultipleTableTypes" class="w-100">
                    <p class="fs-16 font-weight-light-bold">Select Table for Primary Registrant:</p>
                    <v-select
                      :model-value="primaryRegistrantSelectionKey"
                      :items="tableGuestDropdownItems"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      class="mb-4"
                      hide-details
                      density="compact"
                      @update:model-value="handlePrimaryTableDropdownChange"
                    >
                      <template #item="{ props, item }">
                        <v-list-item
                          v-bind="props"
                          :title="item.raw.title"
                          :disabled="item.raw.disabled"
                        ></v-list-item>
                      </template>
                    </v-select>
                  </div>
                </v-col>
                <v-expansion-panels v-model="openChildPanel" multiple>
                  <v-expansion-panel :value="primaryRegistrantTable?._instanceId">
                    <v-expansion-panel-title>
                      <v-row no-gutters>
                        <v-col>
                          <h2 class="fs-16 font-weight-light-bold">Seat 1</h2>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <NewRegistrationForm
                        v-if="
                          primaryRegistrantTable &&
                          ticketMap &&
                          ticketMap[primaryRegistrantTable.table_id]
                        "
                        :key="primaryRegistrantTable._instanceId"
                        @save-registration="
                          handleRegistrationSave($event, primaryRegistrantTable._instanceId)
                        "
                        @stash-state="handleStashState($event, primaryRegistrantTable._instanceId)"
                        :eventData="eventData"
                        :isGroupHostCheckoutFlow="true"
                        :ticket-config="ticketMap[primaryRegistrantTable.table_id]"
                        :saved-data="stashedData[primaryRegistrantTable._instanceId]"
                        :allCustomQuestions="allCustomQuestions"
                        :allMealChoices="allMealChoices"
                        :isPrimary="true"
                      />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>

        <template v-if="additionalTableGuests.length > 0">
          <v-expansion-panel class="mb-6" :value="1">
            <v-expansion-panel-title>
              <v-row no-gutters>
                <v-col>
                  <h2 class="fs-18 font-weight-medium mb-2">Additional Registrations</h2>
                </v-col>
              </v-row>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-0 pa-md-4">
              <template v-for="(guest, i) in additionalTableGuests" :key="guest._instanceId">
                <div class="mt-4">
                  <v-row no-gutters justify="space-between" align="center">
                    <div class="d-flex align-left items-center justify-start">
                      <div class="mr-3">
                        <v-tooltip location="top">
                          <template #activator="{ props }">
                            <v-icon
                              v-bind="props"
                              :color="!!stashedData[guest._instanceId] ? 'success' : 'warning'"
                              size="small"
                            >
                              {{
                                !!stashedData[guest._instanceId]
                                  ? 'mdi-check-circle'
                                  : 'mdi-alert-circle'
                              }}
                            </v-icon>
                          </template>
                          <span>
                            {{
                              !!stashedData[guest._instanceId]
                                ? 'Form Completed'
                                : 'Form Not Completed'
                            }}
                          </span>
                        </v-tooltip>
                      </div>
                      <h2 class="mb-4 fs-16 font-weight-medium font-italic">Guest {{ i + 2 }}:</h2>
                    </div>
                    <v-btn
                      icon="mdi-minus-circle"
                      size="small"
                      color="#DC2626"
                      variant="text"
                      style="box-shadow: none"
                      @click.stop="handleDeleteTableGuest(guest)"
                    ></v-btn>
                  </v-row>

                  <div v-if="hasMultipleTableTypes" class="w-100">
                    <p class="fs-16 font-weight-light-bold">Select Table for Guest:</p>
                    <v-select
                      :model-value="guest.selectedTicketKey"
                      :items="tableGuestDropdownItems"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      class="mb-4"
                      hide-details
                      density="compact"
                      placeholder="Select a table type"
                      @update:model-value="
                        (newKey) => handleTableGuestDropdownChange(guest, newKey)
                      "
                    >
                      <template #item="{ props, item }">
                        <v-list-item
                          v-bind="props"
                          :title="item.raw.title"
                          :disabled="
                            item.raw.disabled && guest.selectedTicketKey !== item.raw.value
                          "
                        ></v-list-item>
                      </template>
                    </v-select>
                  </div>

                  <v-expansion-panels
                    v-if="guest.selectedTicketKey || !hasMultipleTableTypes"
                    v-model="openChildPanel"
                    multiple
                  >
                    <v-expansion-panel :value="guest._instanceId" class="mt-2 mb-6">
                      <v-expansion-panel-title>
                        <v-row no-gutters>
                          <v-col>
                            <h2 class="fs-16 font-weight-bold">Seat {{ i + 2 }}</h2>
                          </v-col>
                        </v-row>
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <NewRegistrationForm
                          v-if="
                            ticketMap &&
                            localSelectionsMap[
                              guest.selectedTicketKey || primaryRegistrantSelectionKey
                            ]
                          "
                          :key="guest._instanceId"
                          @save-registration="handleRegistrationSave($event, guest._instanceId)"
                          @stash-state="handleStashState($event, guest._instanceId)"
                          :eventData="eventData"
                          :isGroupHostCheckoutFlow="true"
                          :ticket-config="
                            guestTicketConfig(
                              localSelectionsMap[
                                guest.selectedTicketKey || primaryRegistrantSelectionKey
                              ].ticket.table_id,
                            )
                          "
                          :saved-data="stashedData[guest._instanceId]"
                          :copy-source-data="stashedData[primaryRegistrantTable?._instanceId]"
                          :copy-source-registration="
                            registrations[primaryRegistrantTable?._instanceId]
                          "
                          copy-button-label="Copy from host"
                          :allCustomQuestions="allCustomQuestions"
                          :allMealChoices="allMealChoices"
                          :isPrimary="false"
                        />
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </template>
      </v-expansion-panels>

      <div v-if="totalTableSeats > 0" class="mt-6 flex flex-column align-start justify-center">
        <p class="fs-16 ml-4">
          Additional registrations can be completed by using the button below or they can be
          completed via the Attendee Hub later.
        </p>
        <v-btn
          color="#0E6CB3"
          variant="text"
          class="text-transform-none mt-3 font-weight-light-bold fs-14"
          prepend-icon="mdi-plus-circle"
          @click.stop="addTableGuest"
          :disabled="remainingTableSeats <= 0"
        >
          Complete another registration ({{ remainingTableSeats }} available)
        </v-btn>
      </div>
    </v-col>

    <v-col class="pa-3" sm="12" md="4">
      <div v-if="allTicketSelectionData && localSelectionsMap" class="d-none d-lg-block">
        <v-row no-gutters justify="center" class="mb-5">
          <h2 class="fs-24">SELECT YOUR REGISTRATION</h2>
        </v-row>
        <TicketCard
          v-for="ticket of visibleTicketSelection"
          :key="ticket.ticket_id"
          :event-data="eventData"
          :ticket="ticket"
          :selected-tickets="localSelectionsMap"
          :qty="localSelectionsMap[`${ticket.table_id}_${ticket.ticket_id}`]?.qty || 0"
          @update:qty="(qty, revert) => handleQtyChange(ticket, qty, revert)"
        />
        <div v-if="allTicketSelectionData.length > ticketsToShow">
          <v-btn
            block
            color="#A3A3A3"
            variant="text"
            class="mt-2 text-transform-none"
            @click.stop="ticketsToShow += 2"
            append-icon="mdi-chevron-down"
          >
            Show more
          </v-btn>
        </div>
      </div>
      <v-divider class="my-8"></v-divider>

      <RegistrationSummary
        v-if="
          displayTableSummary?.length ||
          (displayAddOnSummary && Object.keys(displayAddOnSummary).length)
        "
        :displayTicketSummary="displayTableSummary"
        :displayAddOnSummary="displayAddOnSummary"
        :eventData="eventData"
        :registrations="Object.values(registrations)"
        :disableContinue="disableContinue || isSubmitting"
        :onContinue="handleContinueToCheckout"
      />
    </v-col>
  </v-row>

  <BaseModal
    :modal-open="openDeleteStashedFormConfirm"
    max-width="26%"
    :hide-header="true"
    :custom-actions="false"
    persistent
  >
    <template v-slot:default>
      <v-row no-gutters>
        <v-col sm="2" class="d-flex justify-center pt-2">
          <img
            style="
              background-color: var(--fundeasy-colors-primary-100);
              border-radius: 24px;
              height: 40px;
              width: 40px;
            "
            :src="AlertIcon"
            alt="alert"
          />
        </v-col>
        <v-col class="pt-2">
          <div class="d-flex justify-space-between align-center mb-4">
            <h3
              style="font-weight: 500; font-size: 18px; color: var(--fundeasy-colors-neutral-900)"
            >
              Remove registration selection
            </h3>
            <v-btn
              @click.stop="handleCancelDeleteModal"
              icon="mdi-close"
              size="larger"
              style="box-shadow: none"
            ></v-btn>
          </div>
          <p
            style="font-size: 14px; font-weight: 400; color: var(--fundeasy-colors-neutral-600)"
            class="mb-4"
          >
            Registrant Information has already been saved. If you remove your registration selection
            now, your data will be lost.
          </p>
          <p style="font-size: 14px; font-weight: 400; color: var(--fundeasy-colors-neutral-600)">
            Are you sure you want to continue?
          </p>
        </v-col>
      </v-row>
    </template>
    <template v-slot:buttons>
      <v-row no-gutters justify="end">
        <v-btn
          class="mr-2 text-transform-none"
          variant="text"
          color="success"
          @click.stop="handleCancelDeleteModal"
          >No</v-btn
        >
        <v-btn
          elevation="1"
          variant="elevated"
          color="error"
          class="text-transform-none"
          @click.stop="handleDeleteStashedForm"
          >Yes, delete</v-btn
        >
      </v-row>
    </template>
  </BaseModal>
</template>

<script setup>
import BaseModal from '@/components/modals/BaseModal';
import isEmpty from 'lodash/isEmpty';
import { computed, ref, watch } from 'vue';
import AlertIcon from '@/assets/Icons/AlertIcon.svg';

import { useEventTableRegistration } from '@/composables/attendance/useEventTableRegistration';

const props = defineProps({
  allTicketSelectionData: {
    type: Array,
    default: () => [],
  },
  displayTicketName: {
    type: Function,
    required: true,
  },
  loadingTicketData: {
    type: Boolean,
    default: true,
  },
  ticketMap: {
    type: Object,
    default: () => ({}),
  },
  addonSummary: {
    type: Object,
    default: () => ({}),
  },
  postOrderDetails: {
    type: Function,
    required: true,
  },
  eventData: {
    type: Object,
    default: () => ({}),
  },
  route: {
    type: Object,
    required: true,
  },
  allCustomQuestions: {
    type: Array,
    default: () => [],
  },
  allMealChoices: {
    type: Array,
    default: () => [],
  },
});

const {
  // State
  localSelectionsMap,
  registrations,
  stashedData,
  primaryRegistrantSelectionKey,
  openDeleteStashedFormConfirm,
  isSubmitting,
  // Computed
  tables,
  primaryRegistrantTable,
  hasMultipleTableTypes,

  displayAddOnSummary,
  displayTableSummary,
  additionalTableGuests,
  totalTableSeats,
  remainingTableSeats,
  tableGuestDropdownItems,

  disableContinue,
  // Methods
  handleQtyChange,
  handleRegistrationSave: _handleRegistrationSave,
  handleStashState,
  handleDeleteStashedForm,
  handleCancelDeleteModal,
  handleContinueToCheckout,
  addTableGuest,
  handleDeleteTableGuest,
  handleTableGuestDropdownChange,
  handlePrimaryTableDropdownChange,
} = useEventTableRegistration(
  computed(() => props.allTicketSelectionData),
  computed(() => props.loadingTicketData),
  computed(() => props.addonSummary),
  props.postOrderDetails,
  props.route,
);

const ticketsToShow = ref(3);
const openExpansion = ref(null);
const openChildPanel = ref([]);

// For additional guests, use only the ticket-level registration_form_configuration.
// table_form_configuration contains fields meant only for the primary (host) registrant.
const guestTicketConfig = computed(() => {
  if (!props.ticketMap) return null;
  return (tableId) => {
    const config = props.ticketMap[tableId];
    if (!config) return null;
    return { ...config, table_form_configuration: null };
  };
});

const visibleTicketSelection = computed(() => {
  return props.allTicketSelectionData.slice(0, ticketsToShow.value);
});

watch(tables, (newTickets) => {
  if (newTickets.length > 0 && openExpansion.value === null) {
    openExpansion.value = [0, 1];
    if (primaryRegistrantTable.value) {
      openChildPanel.value = [primaryRegistrantTable.value._instanceId];
    }
  } else if (newTickets.length === 0) {
    openExpansion.value = null;
    openChildPanel.value = [];
  }
});

watch(primaryRegistrantTable, (newTicket) => {
  if (newTicket) {
    if (!openChildPanel.value.includes(newTicket._instanceId)) {
      openChildPanel.value = [newTicket._instanceId];
    }
  } else {
    openChildPanel.value = [];
  }
});

const handleRegistrationSave = (ticketRegistration, instanceId) => {
  const saveSuccess = _handleRegistrationSave(ticketRegistration, instanceId);

  if (saveSuccess) {
    // Get all form instances (primary + guests)
    const allInstances = [
      ...(primaryRegistrantTable.value ? [primaryRegistrantTable.value] : []),
      ...additionalTableGuests.value,
    ];

    const currentIndex = allInstances.findIndex((item) => item._instanceId === instanceId);

    if (currentIndex > -1 && currentIndex + 1 < allInstances.length) {
      const nextInstanceId = allInstances[currentIndex + 1]._instanceId;
      openChildPanel.value = [nextInstanceId];
    } else {
      openChildPanel.value = [];
    }
  }
};
</script>
